import React from "react";
import { Input, InputGroup } from "reactstrap";

/**
 * Coerce a Number input's raw event string to what gets stored (SCRUM-1311,
 * on the decision recorded in SCRUM-693: a Number variable's value is a real
 * number, not "5").
 *
 * Three cases, and the first two are the ones that bite:
 *  - blank (empty or whitespace-only) passes through UNCHANGED. `Number("")`
 *    is 0, and required-validation keys on the value being empty, so casting
 *    a cleared field would silently turn it into a valid 0.
 *  - a value that does not parse to a finite number passes through unchanged
 *    as the original string, so a decimal comma ("3,5" -> NaN) is never
 *    stored as NaN.
 *  - anything else becomes the number.
 *
 * Exported so a test can drive the boundary directly, without a DOM event.
 */
export function toStoredNumber(raw: unknown): unknown {
  if (typeof raw !== "string" || raw.trim() === "") return raw;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : raw;
}

/**
 * Number building block (SCRUM-551, VariableField registry).
 *
 * Contract: receives the variable entry, reports only the clean value via
 * `onChange(name, value)`. Since SCRUM-1311 that value is coerced to a real
 * number at this boundary — see toStoredNumber above for the blank and
 * unparseable cases. This block is shared by the page-content surfaces
 * (create wizard + update card) and the Global Tools settings-variable
 * surface, so all three now store the same shape; the tests that used to pin
 * the string quirk (contentVariables.types, createPage.savePayload,
 * settingsVariables.parsing) were updated with it.
 *
 * Controlled, like HandleOptional's current Number input. NOTE:
 * HandleRequired's legacy Number input is UNcontrolled (no prefill on the
 * update card) — that screen keeps its own block until step 5 decides
 * whether prefill is a bug-fix or must stay.
 */
export type NumberFieldProps = {
  settingVar: { variable: string; value?: string | number };
  onChange: (name: string, value: unknown) => void;
};

export default function NumberField({ settingVar, onChange }: NumberFieldProps) {
  return (
    <InputGroup className="input-group-merge">
      <Input
        placeholder={`insert value for ${settingVar.variable}`}
        type="number"
        min={0}
        value={settingVar.value}
        name={settingVar.variable}
        onChange={(e) => onChange(settingVar.variable, toStoredNumber(e.target.value))}
      />
    </InputGroup>
  );
}
